import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@lims/permissions';
import { updateSampleSchema } from '@lims/shared-validation';
import {
  sampleReceiptsApi,
  samplesApi,
  sampleTypeSettingsApi,
  workOrdersApi,
  type BilingualPair,
  type Sample,
} from '../../api/endpoints.js';
import { ApiError } from '../../api/client.js';
import { useAuth } from '../../app/auth-context.js';
import { useLaboratoryTimeZone } from '../../app/laboratory-time-zone.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  CheckboxGroupField,
  DateTimeField,
  Dialog,
  LoadingState,
  NumberField,
  SaveErrorNotice,
  SearchSelectField,
  SelectField,
  TextAreaField,
  TextField,
  useSaveErrors,
  useSaveFailureFocus,
  useToast,
  validationErrors,
} from '../../components/ui/index.js';
import { ErrorNotice } from '../../components/ErrorNotice.js';
import { currentLocale } from '../../i18n/index.js';
import { requestedTestLabel, requestedTestName, requestedTestStandard } from './requested-tests.js';
import {
  AdditionalFieldsInputs,
  activeFields,
  additionalErrorText,
  draftFromStored,
  payloadValue,
  pickLabel,
  useSampleTypes,
  type AdditionalDraft,
} from './sample-type-fields.js';

/**
 * Edit Sample (D222-D228): every fact of the sample that may be edited, and
 * its requested tests, saved with ONE request.
 *
 * Mounted only while open and filled ONCE from the sample as it stands when
 * opened, so a refetch while the dialog is open never overwrites what the
 * person typed. A failed save keeps every value and the whole selection,
 * marks the fields the server named — including each requested test that
 * could not be removed — and focuses the first (D205, D228). Only a
 * successful save closes it.
 *
 * ADDITIONAL DETAILS FOLLOW THE SAMPLE'S TYPE (0024, D301). The type's active
 * fields are editable; a field archived since shows its value read-only. A
 * material corrected to another type clears the old type's details IN THE
 * SAME SAVE, said on the dialog first; the history keeps what they were.
 *
 * Never offered: the Sample ID, the arrival time and the work order (D223).
 * Requested tests come only from the sample's own work order; the server
 * checks every rule again.
 */

interface EditValues {
  readonly sampleType: string | null;
  readonly quantity: number | null;
  readonly quantityUnit: string;
  readonly description: string;
  readonly sourceDescription: string;
  readonly clientReference: string;
  readonly sampledAt: string | null;
  readonly sampledBy: string;
  readonly conditionOnReceipt: string;
  readonly storageLocation: string;
  readonly notes: string;
  readonly receivedFrom: string;
  /** Additional details as typed, per type code, then by field id (0024). */
  readonly extra: Readonly<Record<string, AdditionalDraft>>;
  readonly requestedServiceIds: readonly string[];
  /** A sample without a work order: the one chosen to link it to (D324). */
  readonly linkWorkOrder: LinkChoice | null;
}

/** A work order chosen to link a sample to, with its number for display. */
interface LinkChoice {
  readonly id: string;
  readonly number: string;
}

/** One requested-test choice: a service of the sample's work order. */
interface Choice {
  readonly id: string;
  readonly lineNumber: number;
  readonly description: BilingualPair;
  readonly referenceStandard: string | null;
  readonly standardVersion: string | null;
}

/** The laboratory's configurable required fields, as the form names them (D70). */
const CONFIGURED_FIELDS: Readonly<Record<string, keyof EditValues>> = {
  client_reference: 'clientReference',
  source_description: 'sourceDescription',
  sampled_at: 'sampledAt',
  sampled_by: 'sampledBy',
  condition_on_receipt: 'conditionOnReceipt',
  storage_location: 'storageLocation',
};

const IN_USE_PREFIX = 'requestedServiceIds.';

function valuesOf(sample: Sample): EditValues {
  return {
    sampleType: sample.sampleType,
    quantity: sample.quantity,
    quantityUnit: sample.quantityUnit,
    description: sample.description ?? '',
    sourceDescription: sample.sourceDescription ?? '',
    clientReference: sample.clientReference ?? '',
    sampledAt: sample.sampledAt,
    sampledBy: sample.sampledBy ?? '',
    conditionOnReceipt: sample.conditionOnReceipt ?? '',
    storageLocation: sample.storage?.location ?? '',
    notes: sample.notes ?? '',
    receivedFrom: sample.receivedFrom,
    extra: { [sample.sampleType]: draftFromStored(sample.additionalFields) },
    requestedServiceIds: sample.requestedServices.map((service) => service.workOrderServiceId),
    linkWorkOrder: null,
  };
}

/** An empty entry is absent, never an empty fact (CLAUDE.md section 8). */
const optional = (value: string) => (value.trim() === '' ? null : value.trim());

export function SampleEditDialog({
  sample,
  onClose,
}: {
  readonly sample: Sample;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const { show } = useToast();
  const queryClient = useQueryClient();
  const locale = currentLocale();
  const timeZone = useLaboratoryTimeZone();
  const [values, setValues] = useState<EditValues>(() => valuesOf(sample));
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});
  const bodyRef = useRef<HTMLDivElement>(null);
  const noticeRef = useRef<HTMLDivElement>(null);

  const set = <Key extends keyof EditValues>(key: Key, value: EditValues[Key]) => {
    setValues((current) => ({ ...current, [key]: value }));
    if (localErrors[key]) {
      setLocalErrors((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
    }
  };

  // Reading the rules needs no extra permission: whoever edits a sample needs
  // to know what this laboratory insists on for its material.
  const typeSettings = useQuery({
    queryKey: ['sample-type-settings'],
    queryFn: () => sampleTypeSettingsApi.list(),
  });
  const sampleTypes = useSampleTypes();
  const required = new Set<string>(
    typeSettings.data?.find((entry) => entry.sampleType === values.sampleType)?.requiredFields ?? [],
  );

  // The receiving desk reads the job's services through the receiving
  // context; anybody else through the work order itself. Either way only the
  // name and the standard are used here — never a price. A sample received
  // without a work order reads the one chosen to link it to (D324).
  const linking = sample.workOrderId === null;
  const workOrderId = sample.workOrderId ?? values.linkWorkOrder?.id ?? null;
  const jobNumber = sample.jobNumber ?? values.linkWorkOrder?.number ?? null;
  const mayReadContext = can(PERMISSIONS.SAMPLES_RECEIVE);
  const choices = useQuery({
    queryKey: ['requested-test-choices', workOrderId, mayReadContext],
    queryFn: async (): Promise<Choice[]> =>
      mayReadContext
        ? (await sampleReceiptsApi.workOrderContext(workOrderId as string)).services
        : (await workOrdersApi.get(workOrderId as string)).services.filter(
            (service) => service.state !== 'cancelled',
          ),
    enabled: workOrderId !== null,
  });
  // The services not cancelled, plus any already requested: keeping one
  // requests nothing new (D226).
  const offered: Choice[] = [...(choices.data ?? [])];
  for (const service of sample.requestedServices) {
    if (offered.some((choice) => choice.id === service.workOrderServiceId)) continue;
    offered.push({
      id: service.workOrderServiceId,
      lineNumber: service.lineNumber,
      description: { ar: service.descriptionAr, en: service.descriptionEn },
      referenceStandard: service.referenceStandard,
      standardVersion: service.standardVersion,
    });
  }
  offered.sort((a, b) => a.lineNumber - b.lineNumber);
  const labelOf = (choice: Choice) =>
    requestedTestLabel(
      requestedTestName(choice.description.ar, choice.description.en, locale),
      requestedTestStandard(choice.referenceStandard, choice.standardVersion),
      t('common.notRecorded'),
    );

  const materialChanged = values.sampleType !== sample.sampleType;
  const selectedType = values.sampleType ? sampleTypes.byCode.get(values.sampleType) : undefined;
  const editableFields = activeFields(selectedType);
  // A field archived since keeps its value on this sample, shown read-only.
  const archivedWithValue = materialChanged
    ? []
    : (selectedType?.fields ?? []).filter(
        (field) => field.archived && sample.additionalFields.some((value) => value.fieldId === field.id),
      );
  const draft = (values.sampleType ? values.extra[values.sampleType] : undefined) ?? {};
  /**
   * D301: details of the OLD type cannot stay on a sample of another type. They
   * are cleared in the same save, said on the dialog first, and the history
   * keeps what they were.
   */
  const clearsAdditional = materialChanged && sample.additionalFields.length > 0;
  const storedOptionLabels = Object.fromEntries(
    sample.additionalFields
      .filter((value) => value.option)
      .map((value) => [value.option?.id as string, pickLabel(value.option?.labelAr, value.option?.labelEn, locale)]),
  );
  const setExtra = (fieldId: string, value: string) => {
    if (!values.sampleType) return;
    set('extra', { ...values.extra, [values.sampleType]: { ...draft, [fieldId]: value } });
  };

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => samplesApi.update(sample.id, body),
    onSuccess: async (saved) => {
      queryClient.setQueryData(['sample', sample.id], saved);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['sample', sample.id] }),
        queryClient.invalidateQueries({ queryKey: ['samples'] }),
      ]);
      show({
        tone: 'success',
        message: t(saved.sampleType !== sample.sampleType ? 'sampleEdit.savedReprint' : 'sampleEdit.saved'),
      });
      onClose();
    },
  });
  const server = useSaveErrors(save.error);
  useSaveFailureFocus(save.error, bodyRef, noticeRef);

  /** The message for a field: the local check first, then the server's. */
  const errorOf = (field: keyof EditValues): string | undefined => {
    if (localErrors[field]) return localErrors[field];
    if (save.isPending) return undefined;
    const configured = Object.keys(CONFIGURED_FIELDS).find((key) => CONFIGURED_FIELDS[key] === field);
    return server.fields[field] ?? (configured ? server.fields[configured] : undefined);
  };
  /** The server's refusals of additional details, by field id. */
  const additionalErrors: Record<string, string> = {};
  if (!save.isPending && save.error instanceof ApiError) {
    for (const detail of save.error.details) {
      const match = detail.field ? /^additionalFields\.(.+)$/.exec(detail.field) : null;
      if (match?.[1]) additionalErrors[match[1]] = additionalErrorText(t, detail.code ?? '');
    }
  }
  const inUse = new Set(
    Object.keys(server.fields)
      .filter((field) => field.startsWith(IN_USE_PREFIX))
      .map((field) => field.slice(IN_USE_PREFIX.length)),
  );

  /**
   * The type's active fields as they now stand (null clears one), and on a
   * material change every old value cleared by name. The server compares, so
   * an unchanged value records nothing.
   */
  function additionalBody(): Record<string, string | boolean | null> | null {
    const body: Record<string, string | boolean | null> = {};
    if (materialChanged) {
      for (const value of sample.additionalFields) body[value.fieldId] = null;
    }
    for (const field of editableFields) {
      const value = payloadValue(field, draft[field.id]);
      if (value !== null || !materialChanged) body[field.id] = value;
    }
    return Object.keys(body).length > 0 ? body : null;
  }

  function submit() {
    const additional = additionalBody();
    const body: Record<string, unknown> = {
      // D324: the link and its tests travel with the rest of the edit, and
      // are saved in the same transaction.
      ...(linking && workOrderId !== null ? { workOrderId } : {}),
      sampleType: values.sampleType,
      description: optional(values.description),
      ...(additional ? { additionalFields: additional } : {}),
      ...(values.quantity !== null ? { quantity: values.quantity } : {}),
      quantityUnit: values.quantityUnit.trim(),
      receivedFrom: values.receivedFrom.trim(),
      sourceDescription: optional(values.sourceDescription),
      clientReference: optional(values.clientReference),
      sampledAt: values.sampledAt,
      sampledBy: optional(values.sampledBy),
      conditionOnReceipt: optional(values.conditionOnReceipt),
      notes: optional(values.notes),
      // Where a disposed sample was kept is history, not an edit (D222).
      ...(sample.isDisposed ? {} : { storageLocation: optional(values.storageLocation) }),
      ...(workOrderId !== null ? { requestedServiceIds: [...values.requestedServiceIds] } : {}),
    };
    const problems = validationErrors(updateSampleSchema, body, t);
    if (values.sampleType === null) problems.sampleType = t('validation.required');
    if (values.quantity === null) problems.quantity = t('validation.required');
    if (
      workOrderId !== null &&
      values.requestedServiceIds.length === 0 &&
      (sample.requestedServices.length > 0 || (linking && offered.length > 0))
    ) {
      problems.requestedServiceIds = t('sampleRequestedTests.chooseOne');
    }
    setLocalErrors(problems);
    if (Object.keys(problems).length > 0) return;
    save.mutate(body);
  }

  const fieldLabels: Record<string, string> = {
    sampleType: t('samples.sampleTypeLabel'),
    quantity: t('samples.quantity'),
    quantityUnit: t('samples.quantityUnit'),
    receivedFrom: t('samples.receivedFromLabel'),
    notes: t('samples.notes'),
    description: t('receiving.sampleDescription'),
    requestedServiceIds: t('receiving.requestedTests'),
    workOrderId: t('sampleEdit.workOrder'),
  };
  for (const [configured, field] of Object.entries(CONFIGURED_FIELDS)) {
    fieldLabels[configured] = t(`receivingField.${configured}`);
    fieldLabels[field] = t(`receivingField.${configured}`);
  }
  for (const choice of offered) fieldLabels[`${IN_USE_PREFIX}${choice.id}`] = labelOf(choice);
  for (const type of sampleTypes.types) {
    for (const field of type.fields) {
      fieldLabels[`additionalFields.${field.id}`] = pickLabel(field.labelAr, field.labelEn, locale) || field.key;
    }
  }

  const inUseError = inUse.size > 0 ? t('fieldErrors.requested_service_in_use') : undefined;
  const requestedError = localErrors.requestedServiceIds ?? inUseError ?? errorOf('requestedServiceIds');

  return (
    <Dialog
      isOpen
      onClose={onClose}
      title={t('sampleEdit.title', { number: sample.sampleNumber })}
      size="xl"
      footer={
        <ActionBar>
          <ActionButton priority="tertiary" onPress={onClose} isDisabled={save.isPending}>
            {t('common.cancel')}
          </ActionButton>
          <ActionButton
            priority="primary"
            isLoading={save.isPending}
            isDisabled={workOrderId !== null && choices.isLoading}
            onPress={submit}
          >
            {t('common.save')}
          </ActionButton>
        </ActionBar>
      }
    >
      <div ref={bodyRef} className="flex min-w-0 flex-col gap-stack" data-testid="sample-edit-form">
        <p className="text-caption text-default-600">{t('sampleEdit.fixedNotice')}</p>

        <div className="grid min-w-0 grid-cols-1 gap-x-gutter gap-y-stack lg:grid-cols-2">
          <SelectField
            label={t('samples.sampleTypeLabel')}
            value={values.sampleType}
            onChange={(sampleType) => set('sampleType', sampleType)}
            isRequired
            placeholder={t('samples.sampleTypePlaceholder')}
            isLoading={sampleTypes.isLoading}
            // An archived type is not offered, except as the one this sample has.
            options={sampleTypes.types
              .filter((type) => !type.archived || type.code === sample.sampleType)
              .map((type) => ({ value: type.code, label: sampleTypes.nameOf(type.code) }))}
            {...(errorOf('sampleType') ? { error: errorOf('sampleType') as string } : {})}
          />
          <div className="grid min-w-0 grid-cols-2 gap-x-gutter">
            <NumberField
              label={t('samples.quantity')}
              value={values.quantity}
              onChange={(quantity) => set('quantity', quantity)}
              isRequired
              step="any"
              {...(errorOf('quantity') ? { error: errorOf('quantity') as string } : {})}
            />
            <TextField
              label={t('samples.quantityUnit')}
              value={values.quantityUnit}
              onChange={(quantityUnit) => set('quantityUnit', quantityUnit)}
              isRequired
              {...(errorOf('quantityUnit') ? { error: errorOf('quantityUnit') as string } : {})}
            />
          </div>
          {materialChanged ? (
            <div className="lg:col-span-2">
              <Alert
                tone="warning"
                title={t('sampleEdit.materialChangedTitle')}
                description={
                  clearsAdditional
                    ? `${t('sampleEdit.materialChangedBody')} ${t('sampleEdit.additionalCleared', { count: sample.additionalFields.length })}`
                    : t('sampleEdit.materialChangedBody')
                }
              />
            </div>
          ) : null}
          <div className="lg:col-span-2">
            <TextField
              label={t('receiving.sampleDescription')}
              description={t('receiving.sampleDescriptionHint')}
              value={values.description}
              onChange={(description) => set('description', description)}
              {...(errorOf('description') ? { error: errorOf('description') as string } : {})}
            />
          </div>
          {editableFields.length + archivedWithValue.length > 0 ? (
            <section
              className="min-w-0 lg:col-span-2"
              aria-labelledby="sample-edit-additional"
              data-testid="sample-edit-additional"
            >
              <h3 id="sample-edit-additional" className="mb-stack text-body font-semibold text-default-900">
                {t('receiving.additionalSection')}
              </h3>
              <AdditionalFieldsInputs
                fields={[...editableFields, ...archivedWithValue]}
                values={draft}
                onChange={setExtra}
                errors={additionalErrors}
                storedOptions={storedOptionLabels}
                readOnlyIds={new Set(archivedWithValue.map((field) => field.id))}
              />
            </section>
          ) : null}
          <TextField
            label={t('receivingField.source_description')}
            value={values.sourceDescription}
            onChange={(sourceDescription) => set('sourceDescription', sourceDescription)}
            isRequired={required.has('source_description')}
            {...(errorOf('sourceDescription') ? { error: errorOf('sourceDescription') as string } : {})}
          />
          <TextField
            label={t('receivingField.client_reference')}
            description={t('receiving.sampleReferenceHint')}
            value={values.clientReference}
            onChange={(clientReference) => set('clientReference', clientReference)}
            isRequired={required.has('client_reference')}
            {...(errorOf('clientReference') ? { error: errorOf('clientReference') as string } : {})}
          />
          <DateTimeField
            label={t('receivingField.sampled_at')}
            description={t('samples.sampledAtHint')}
            value={values.sampledAt}
            onChange={(sampledAt) => set('sampledAt', sampledAt)}
            timeZone={timeZone}
            isRequired={required.has('sampled_at')}
            {...(errorOf('sampledAt') ? { error: errorOf('sampledAt') as string } : {})}
          />
          <TextField
            label={t('receivingField.sampled_by')}
            value={values.sampledBy}
            onChange={(sampledBy) => set('sampledBy', sampledBy)}
            isRequired={required.has('sampled_by')}
            {...(errorOf('sampledBy') ? { error: errorOf('sampledBy') as string } : {})}
          />
          <TextAreaField
            label={t('receivingField.condition_on_receipt')}
            description={t('samples.conditionHint')}
            value={values.conditionOnReceipt}
            onChange={(conditionOnReceipt) => set('conditionOnReceipt', conditionOnReceipt)}
            isRequired={required.has('condition_on_receipt')}
            rows={2}
            {...(errorOf('conditionOnReceipt') ? { error: errorOf('conditionOnReceipt') as string } : {})}
          />
          <TextField
            label={t('receivingField.storage_location')}
            description={t(sample.isDisposed ? 'sampleEdit.storageDisposed' : 'samples.storageLocationHint')}
            value={values.storageLocation}
            onChange={(storageLocation) => set('storageLocation', storageLocation)}
            isRequired={!sample.isDisposed && required.has('storage_location')}
            isReadOnly={sample.isDisposed}
            {...(errorOf('storageLocation') ? { error: errorOf('storageLocation') as string } : {})}
          />
          <TextField
            label={t('samples.receivedFromLabel')}
            value={values.receivedFrom}
            onChange={(receivedFrom) => set('receivedFrom', receivedFrom)}
            isRequired
            {...(errorOf('receivedFrom') ? { error: errorOf('receivedFrom') as string } : {})}
          />
          <div className="lg:col-span-2">
            <TextAreaField
              label={t('samples.notes')}
              value={values.notes}
              onChange={(notes) => set('notes', notes)}
              rows={2}
              {...(errorOf('notes') ? { error: errorOf('notes') as string } : {})}
            />
          </div>
        </div>

        <section
          aria-labelledby="sample-edit-work-order"
          className="flex min-w-0 flex-col gap-2"
          data-testid="sample-edit-work-order"
        >
          <h3 id="sample-edit-work-order" className="text-body font-semibold text-default-900">
            {t('sampleEdit.workOrder')}
          </h3>
          {linking ? (
            <>
              <p className="text-body text-default-700" data-testid="sample-edit-link-intro">
                {t('sampleEdit.linkIntro')}
              </p>
              <LinkWorkOrderField
                value={values.linkWorkOrder}
                onChange={(next) => {
                  // A service belongs to exactly one work order: a different
                  // choice starts its tests again. Everything else typed stays.
                  setValues((current) => ({ ...current, linkWorkOrder: next, requestedServiceIds: [] }));
                  setLocalErrors((current) => {
                    const rest = { ...current };
                    delete rest.requestedServiceIds;
                    return rest;
                  });
                }}
                {...(!save.isPending && server.fields.workOrderId ? { error: server.fields.workOrderId } : {})}
              />
            </>
          ) : (
            <p className="text-body text-default-800" data-testid="sample-edit-work-order-fixed">
              <span dir="ltr" className="font-semibold">{sample.jobNumber}</span>
              <span className="block text-caption text-default-600">{t('sampleEdit.workOrderFixed')}</span>
            </p>
          )}
        </section>

        <section aria-labelledby="sample-edit-tests" className="flex min-w-0 flex-col gap-2">
          <h3 id="sample-edit-tests" className="text-body font-semibold text-default-900">
            {t('receiving.requestedTests')}
          </h3>
          {workOrderId === null ? (
            <p className="text-body text-default-700" data-testid="sample-edit-no-work-order">
              {t('sampleEdit.linkChooseFirst')}
            </p>
          ) : choices.isLoading ? (
            <LoadingState label={t('common.loading')} />
          ) : choices.error ? (
            <ErrorNotice error={choices.error} />
          ) : linking && offered.length === 0 ? (
            <p className="text-body text-default-700">{t('sampleEdit.linkNoServices')}</p>
          ) : (
            <CheckboxGroupField
              label={
                jobNumber
                    ? t('sampleEdit.requestedLabel', { job: jobNumber })
                    : t('sampleEdit.requestedLabelUnnumbered')
              }
              description={t('sampleEdit.requestedHint')}
              value={values.requestedServiceIds}
              onChange={(next) => set('requestedServiceIds', next)}
              {...(requestedError ? { error: requestedError } : {})}
              options={offered.map((choice) => ({
                value: choice.id,
                label: inUse.has(choice.id)
                  ? t('sampleEdit.testInUse', { test: labelOf(choice) })
                  : labelOf(choice),
              }))}
            />
          )}
        </section>

        {save.error ? (
          <div ref={noticeRef} tabIndex={-1} className="outline-none">
            <SaveErrorNotice error={save.error} title={t('sampleEdit.failed')} fieldLabels={fieldLabels} />
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

/**
 * The work order to link a sample received without one to (D324): this
 * laboratory's work orders that are not cancelled, newest first, searched on
 * the server by number, client or project as the person types. Only the
 * choice is made here; the server checks it again when the sample is saved.
 */
function LinkWorkOrderField({
  value,
  onChange,
  error,
}: {
  readonly value: LinkChoice | null;
  readonly onChange: (next: LinkChoice | null) => void;
  readonly error?: string;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [search, setSearch] = useState('');
  // One request per pause in typing, not one per key.
  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(text.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [text]);
  const found = useQuery({
    queryKey: ['sample-work-order-options', 'link', search],
    queryFn: () =>
      samplesApi.workOrderOptions({ purpose: 'link', limit: 20, ...(search ? { search } : {}) }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const rows = found.data?.data ?? [];
  const options = [
    // The choice stays listed while other searches run.
    ...(value && !rows.some((row) => row.id === value.id)
      ? [{ value: value.id, label: value.number }]
      : []),
    ...rows.map((row) => {
      const parties = [row.clientName, row.projectName].filter(Boolean).join(' / ');
      return parties
        ? { value: row.id, label: row.number, description: parties }
        : { value: row.id, label: row.number };
    }),
  ];
  return (
    <SearchSelectField
      label={t('sampleEdit.workOrder')}
      value={value?.id ?? null}
      onChange={(id) => {
        const number = rows.find((row) => row.id === id)?.number ?? value?.number ?? '';
        onChange({ id, number });
      }}
      options={options}
      onSearchChange={setText}
      {...(value ? { selectedLabel: value.number } : {})}
      placeholder={t('sampleEdit.linkPlaceholder')}
      emptyContent={t('sampleEdit.linkEmpty')}
      isLoading={found.isFetching}
      {...(error ? { error } : {})}
    />
  );
}
